import { Input, message, Select, Typography } from 'antd';
import { get } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { Form } from '~/components';
import CustomFields from '~/components/CustomFields';
import WizardForm from '~/components/WizardForm';
import { useGetApplication, useSubmitStep } from '~/controllers/wizard';
import { useGetDraft, useSaveDraft } from '~/controllers/draft';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import { getAuthorizedRepresentativeName } from '~/pages/wizard/application/authorizedRepresentative';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { Person } from '~/types/Person';

import { FormItemContextProvider } from '../Form/FormItemContext';
import AuthorizedRepresentativeFormItems from './authorizedRepresentative/AuthorizedRepresentativeFormItems';

const stepKey = 'shareholders';
const ShareholdersSoleProprietor: React.FC = () => {
  const { t } = useTranslation('fillApplication');

  const applicationQuery = useGetApplication();
  const submitMutation = useSubmitStep();
  const draftMutation = useSaveDraft();

  const [form] = Form.useForm();
  const [customForm] = Form.useForm();

  const representatives: Person[] = useMemo(
    () => get(applicationQuery.data?.payload, 'authorizedRepresentative') || [],
    [applicationQuery.data],
  );

  const draftQuery = useGetDraft<any>();
  const savedUid =
    get(draftQuery.data, 'shareholders.authorizedRepresentative.0.uid') ||
    get(applicationQuery.data?.payload, 'shareholders.authorizedRepresentative.0.uid');
  const foundIndex = representatives.findIndex((person) => person?.uid === savedUid);
  const savedIndex = foundIndex === -1 ? 0 : foundIndex;

  const [overrideIndex, setOverrideIndex] = useState<number | undefined>(undefined);
  const selectedIndex = overrideIndex ?? savedIndex;

  const buildStepData = (index: number) => {
    const person: Person | undefined = get(applicationQuery.data?.payload, getAuthorizedRepresentativeName(index));
    if (!person) return null;
    const authorizedRepresentative: NonNullable<ApplicationPayload['shareholders']>['authorizedRepresentative'] = [
      { uid: person.uid, shares: 100 },
    ];
    const customFields = customForm.getFieldsValue().shareholders?.customFields;
    return {
      shareholders: {
        authorizedRepresentative,
        customFields,
        individuals: [],
        beneficialOwners: [],
        businesses: [],
      },
    };
  };

  const onSelectRepresentative = (index: number) => {
    setOverrideIndex(index);
    const stepData = buildStepData(index);
    if (stepData) {
      draftMutation.mutate({ stepData });
    }
  };

  const onFinish = async () => {
    const stepData = buildStepData(selectedIndex);
    if (stepData) {
      await submitMutation.mutateAsync({ stepData });
    } else {
      void message.warn(t('No authorized representative found'));
    }
  };

  const location = useLocation();

  return (
    <Container>
      <FormItemContextProvider value={{ disabledByDefault: true }}>
        <Typography.Paragraph>
          {t(
            'Since your Company Type was selected as a sole proprietor, the Shareholders section is disabled. You can go to the next step.',
          )}
        </Typography.Paragraph>
        <section className="ShareholderSelect-Section">
          <Typography.Title level={5}>{t('Select the shareholder (100% ownership):')}</Typography.Title>
          <Select
            className="ShareholderSelect"
            size="large"
            value={selectedIndex}
            onChange={(value) => onSelectRepresentative(value)}
            placeholder={t('Select a shareholder')}
            options={representatives.map((person, index) => ({
              value: index,
              label: `${person?.firstName || ''} ${person?.lastName || ''}`.trim() || person?.email || person?.uid,
            }))}
          />
        </section>
        <section>
          <Form onFinish={onFinish} name={location.pathname} form={form} initialValues={applicationQuery.data?.payload}>
            {/* <Collapse className="ShareholdersMultiple-Collapse" defaultActiveKey={['1']} ghost>
              <Collapse.Panel header={t('Shareholder Proof Document')} key="1">
                <Form.Item name={n(['proof', 'documentType'])} label={t('Select the document type')}>
                  <Select options={getProofDocumentOptions()} />
                </Form.Item>
                <Form.Visible name={n(['proof', 'documentType'])} visibleIf={copyMemorandumOrPartnership}>
                  <div className="ShareholdersMultiple-CheckboxItem">
                    <Form.Item
                      name={n(['proof', 'memorandumOfAssociation', 'checked'])}
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>
                        <strong>{t('Memorandum of Association')}</strong>
                      </Checkbox>
                    </Form.Item>
                  </div>

                  <Form.Visible name={n(['proof', 'memorandumOfAssociation', 'checked'])}>
                    <Form.Item name={n(['proof', 'memorandumOfAssociation', 'files'])}>
                      <DocumentInput name="photos" variant="default" />
                    </Form.Item>
                  </Form.Visible>

                  <div className="ShareholdersMultiple-CheckboxItem">
                    <Form.Item name={n(['proof', 'partnershipDeed', 'checked'])} valuePropName="checked" noStyle>
                      <Checkbox>
                        <strong>{t('Articles of association')}</strong>
                      </Checkbox>
                    </Form.Item>
                  </div>

                  <Form.Visible name={n(['proof', 'partnershipDeed', 'checked'])}>
                    <Form.Item name={n(['proof', 'partnershipDeed', 'files'])}>
                      <DocumentInput name="photos" variant="default" />
                    </Form.Item>
                  </Form.Visible>
                </Form.Visible>
              </Collapse.Panel>
            </Collapse> */}
            <AuthorizedRepresentativeFormItems
              key={selectedIndex}
              form={form}
              name={getAuthorizedRepresentativeName(selectedIndex)}
              withFiles={false}
              withProof={false}
              defaultIsAuthorizedSignatory
              withCustomFields={false}
            />
          </Form>
        </section>

        <section>
          <Typography.Title level={5}>{t('Ownership share:')}</Typography.Title>
          <Form>
            <InputsGroupLayout>
              <Form.Item
                label={t('Shares %')}
                name={['shareholders', 'authorizedRepresentative', 'shares']}
                initialValue="100"
              >
                <Input />
              </Form.Item>
            </InputsGroupLayout>
          </Form>
        </section>
      </FormItemContextProvider>
      <WizardForm stepKey={stepKey} form={customForm}>
        <CustomFields />
      </WizardForm>
    </Container>
  );
};

const Container = styled.div`
  .ShareholderSelect-Section {
    margin-bottom: 32px;
  }
  .ShareholderSelect {
    width: 100%;
    max-width: 420px;
    display: block;
  }
`;

// const copyMemorandumOrPartnership = 'Upload a copy of Articles of Association (AOA)/ Memorandum of Association (MOA)';
// const getProofDocumentOptions = () => [
//   {
//     label: i18n.t('Shareholders are listed in company license', { ns: 'fillApplication' }),
//     value: 'Shareholders are listed in company license',
//   },
//   {
//     label: i18n.t(copyMemorandumOrPartnership, { ns: 'fillApplication' }),
//     value: copyMemorandumOrPartnership,
//   },
// ];
// const n = (namePath: string | string[]) => [shareholdersKey].concat([namePath].flat());

export default ShareholdersSoleProprietor;
